import { loggerClient } from './loggerClient';

const levels = ['log', 'info', 'warn', 'error', 'debug'];
const levelsToSend = process.env.REACT_APP_REMOTE_LOGGING_LEVELS?.split(
  ',',
) || ['log', 'info', 'warn', 'error', 'debug'];

const MAX_DEPTH = 3;
const MAX_KEYS = 20;
const MAX_STRING_LENGTH = 2000;

export function safeSerialize(value, depth = MAX_DEPTH, seen = new WeakSet()) {
  // Primitive types
  if (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    if (typeof value === 'string' && value.length > MAX_STRING_LENGTH) {
      return `"${value.slice(0, MAX_STRING_LENGTH)}…"`; // limit long strings
    }
    return String(value);
  }

  // Errors
  if (value instanceof Error) {
    return JSON.stringify({
      name: value.name,
      message: value.message,
      stack: value.stack,
    });
  }

  // DOM / Events
  if (typeof Event !== 'undefined' && value instanceof Event) {
    return JSON.stringify({
      type: value.type,
      target: value.target?.constructor?.name,
    });
  }

  // Functions
  if (typeof value === 'function') {
    return `[Function ${value.name || 'anonymous'}]`;
  }

  // Objects / Arrays
  if (typeof value === 'object') {
    if (seen.has(value)) {
      return '"[Circular]"';
    }

    if (depth <= 0) {
      return Array.isArray(value) ? '"[Array]"' : '"[Object]"';
    }

    seen.add(value);

    // Arrays
    if (Array.isArray(value)) {
      const len = Math.min(value.length, MAX_KEYS);
      const items = [];
      for (let i = 0; i < len; i++) {
        items.push(safeSerialize(value[i], depth - 1, seen));
      }
      if (value.length > len) {
        items.push(`"... ${value.length - len} more items"`);
      }
      return `[${items.join(', ')}]`;
    }

    // Plain objects
    const out = [];
    let count = 0;
    for (const key in value) {
      if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
      if (count >= MAX_KEYS) {
        out.push(`"…": "max keys reached"`);
        break;
      }
      const serializedValue = safeSerialize(value[key], depth - 1, seen);
      out.push(`"${key}": ${serializedValue}`);
      count++;
    }
    return `{${out.join(', ')}}`;
  }

  // Fallback
  try {
    return String(value);
  } catch {
    return '"[Unserializable]"';
  }
}

export const interceptConsole = () => {
  levels.forEach((level) => {
    const original = console[level];

    console[level] = (...args) => {
      original.apply(console, args);

      if (
        !process.env.REACT_APP_REMOTE_LOGGING ||
        !levelsToSend?.includes(level)
      )
        return;

      // Avoid infinite recursion
      if (console.__LOGGER_INTERNAL__) return;

      const safeArgs = args.map((arg) => safeSerialize(arg));
      loggerClient.log(level, safeArgs);
    };
  });
};
